/**
 * VIPGuard — wraps any page/route that requires an active VIP membership.
 *
 * Flow:
 * 1. Unauthenticated → login page
 * 2. Authenticated but no VIP tier → /vip-upgrade
 * 3. VIP but onboarding not completed → /onboarding (non-admins only)
 * 4. VIP + onboarded, hitting /command or /command/dashboard → redirect to operator home
 * 5. All other VIP routes → render children
 *
 * Admins bypass the onboarding check.
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";

// Use window.Clerk directly to avoid @clerk/clerk-react hook context issues
const clerkGlobal = () => (window as any).Clerk;
const isClerkLoaded = () => !!clerkGlobal()?.loaded;
const isClerkSignedIn = () => !!clerkGlobal()?.user;
const openClerkSignIn = (opts?: { fallbackRedirectUrl?: string }) => clerkGlobal()?.openSignIn(opts);

interface VIPGuardProps {
  children: React.ReactNode;
  /** If true, also requires admin role (for admin-only VIP sub-pages) */
  requireAdmin?: boolean;
  /**
   * If true, skip the operator-home redirect when on /command or /command/dashboard.
   * Set this on the VIPDashboard route to avoid an infinite redirect loop.
   */
  skipOperatorRedirect?: boolean;
}

const OP_HOME: Record<string, string> = {
  op1: "/command/op1",
  op2: "/command/op2",
  op3: "/command/op3",
};

export default function VIPGuard({
  children,
  requireAdmin = false,
  skipOperatorRedirect = false,
}: VIPGuardProps) {
  const { user, loading } = useAuth();
  const [location, setLocation] = useLocation();
  const [clerkReady, setClerkReady] = useState(isClerkLoaded());

  // Poll until window.Clerk finishes loading (same idiom as ClerkSessionBridge).
  // openSignIn before ClerkJS is ready throws "components are not ready yet"
  // and crashes cold visitors into the ErrorBoundary instead of a sign-in wall.
  useEffect(() => {
    if (clerkReady) return;
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      if (isClerkLoaded()) {
        setClerkReady(true);
      } else {
        setTimeout(check, 200);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [clerkReady]);

  useEffect(() => {
    if (loading) return;

    // 1. Not logged in → Clerk sign-in (if Clerk has no session either)
    if (!user) {
      if (clerkReady && !isClerkSignedIn()) {
        openClerkSignIn({ fallbackRedirectUrl: window.location.href });
      }
      return;
    }

    // 2. No VIP tier → upgrade page (admins bypass)
    if (!user.vipTier && user.role !== "admin") {
      setLocation("/vip-upgrade");
      return;
    }

    // 3. Admin role check
    if (requireAdmin && user.role !== "admin") {
      setLocation("/");
      return;
    }

    // 4. Onboarding not completed (non-admins only)
    const userAny = user as any;
    if (user.role !== "admin" && !userAny.onboardingCompleted) {
      if (location !== "/onboarding") {
        setLocation("/onboarding");
      }
      return;
    }

    // 5. On /command or /command/dashboard → redirect to operator home
    if (!skipOperatorRedirect && (location === "/command" || location === "/command/dashboard")) {
      const operatorMode = userAny.operatorMode as string | null;
      if (operatorMode && OP_HOME[operatorMode]) {
        setLocation(OP_HOME[operatorMode]);
      }
    }
  }, [loading, user, clerkReady, requireAdmin, skipOperatorRedirect, location, setLocation]);

  // Loading state — also shown to signed-out visitors until ClerkJS is ready
  // to present the sign-in modal (instead of a blank page or a crash).
  if (loading || (!user && !clerkReady)) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "3px solid #1E4D2B",
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <p
          style={{
            color: "#1E4D2B",
            fontFamily: "Montserrat, sans-serif",
            fontSize: "12px",
            letterSpacing: "0.15em",
          }}
        >
          VERIFYING ACCESS
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user || (!user.vipTier && user.role !== "admin")) return null;
  if (requireAdmin && user.role !== "admin") return null;

  const userAny = user as any;
  if (user.role !== "admin" && !userAny.onboardingCompleted) return null;

  return <>{children}</>;
}
